import { inject, Injectable, NgZone } from '@angular/core';
import { App } from '@capacitor/app';
import { ActionPerformed, DeliveredNotifications, PushNotifications, PushNotificationSchema, Token } from '@capacitor/push-notifications';
import {
    ActivityStreamItemType,
    CONFIG_TOKEN,
    IPxlNotificationItem,
    LoginState,
    MedicationPlansGroup,
    Plan,
    PushNotificationProviderTypes,
    ReferenceDataForPlans,
} from '@pxl/models/common';
import {
    ActivityStreamService,
    DialogService,
    LAZY_DIALOG_REGISTRY,
    LoggingService,
    LoginService,
    MedicationPlanService,
    NotificationService,
    PlatformService,
    PushNotificationService,
} from '@pxl/models/contracts';
import {
    AssignmentGroupService as AssignmentGroupBackendService,
    AssignmentService as AssignmentBackendService,
    PushNotificationService as PushNotificationBackendService,
} from '@pxl/shared/generated';
import { toArray } from '@pxl/shared/util';
import { defer, EMPTY, firstValueFrom, from, Observable, of, Subject } from 'rxjs';
import { catchError, defaultIfEmpty, filter, finalize, map, switchMap, take } from 'rxjs/operators';
import { Badge } from '@capawesome/capacitor-badge';

type PxlPushNotificationData = {
    activityStreamItemTypeId?: string;
    activityStreamItemId?: string;
    activityStreamItemGroupId?: string;
    [key: string]: unknown;
};

@Injectable()
export class DefaultPushNotificationService implements PushNotificationService {
    private readonly inAppNotifications$: Subject<unknown> = new Subject();

    private deviceToken = '';

    private readonly loginService = inject(LoginService);
    private readonly platformService = inject(PlatformService);
    private readonly pushNotificationBackendService = inject(PushNotificationBackendService);
    private readonly notificationService = inject(NotificationService);
    private readonly activityStreamService = inject(ActivityStreamService);
    private readonly dialogService = inject(DialogService);
    private readonly medicationPlanService = inject(MedicationPlanService);
    private readonly assignmentBackendService = inject(AssignmentBackendService);
    private readonly assignmentGroupBackendService = inject(AssignmentGroupBackendService);
    private readonly config = inject(CONFIG_TOKEN);
    private readonly ngZone = inject(NgZone);
    private readonly loggingService = inject(LoggingService);
    private readonly lazyDialogs = inject(LAZY_DIALOG_REGISTRY);

    public initPush() {
        if (this.platformService.isNativePlatform()) {
            this.addPushNotificationListener();
            this.registerPush().subscribe();
            this.registerAppListener();

            this.loginService
                .onUserLoginChanged()
                .pipe(
                    filter<LoginState>((state: LoginState) => state.isLoggedIn && state.isTermsOfUseForUserValid),
                    switchMap(() => this.subscribeDeviceForUser()),
                )
                .subscribe();

            this.loginService
                .onBeforeUserLogout()
                .pipe(switchMap((token: string) => this.unsubscribeDevice(token)))
                .subscribe();
        }
    }

    public snoozeAssignmentNotification(assignmentId: string): Observable<void> {
        return this.assignmentBackendService
            .snoozeAssignment(assignmentId, this.config.API_VERSION, this.loginService.getToken() ?? '')
            .pipe(map((): void => undefined));
    }

    public snoozeAssignmentGroupNotification(assignmentGroupId: string): Observable<void> {
        return this.assignmentGroupBackendService
            .snoozeAssignmentGroup(assignmentGroupId, this.config.API_VERSION, this.loginService.getToken() ?? '')
            .pipe(map((): void => undefined));
    }

    private addPushNotificationListener() {
        void PushNotifications.addListener('registrationError', (error: unknown) => {
            this.loggingService.error('Error: ' + JSON.stringify(error));
        });

        void PushNotifications.addListener('registration', (token: Token) => {
            this.deviceToken = token.value;
            this.loggingService.info('DeviceToken: ' + this.deviceToken);
            this.subscribeDeviceForUser().subscribe();
        });

        void PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
            this.ngZone.run(() => {
                this.loggingService.info('PushNotification Received');
                this.loggingService.info(notification);
                from(this.platformService.isActive())
                    .pipe(filter((isAppActive) => isAppActive))
                    .subscribe(() => {
                        this.inAppNotifications$.next(notification);
                        this.notificationService.showNotification(this.createNotificationItem(notification));
                    });
            });
        });

        void PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
            this.ngZone.run(() => {
                this.notificationActionPerformedHandler(notification).subscribe();
            });
        });
    }

    private registerPush(): Observable<void> {
        return defer(() => PushNotifications.checkPermissions()).pipe(
            switchMap((permStatus) => {
                if (permStatus.receive === 'prompt') {
                    return defer(() => PushNotifications.requestPermissions());
                }
                return of(permStatus);
            }),
            switchMap((permStatus) => {
                if (permStatus.receive === 'granted') {
                    return defer(() => PushNotifications.register()).pipe(map((): void => undefined));
                }
                this.loggingService.warn('Push notifications not granted');
                return of(undefined);
            }),
        );
    }

    private subscribeDeviceForUser(): Observable<void> {
        if (!this.deviceToken) {
            return EMPTY;
        }

        return from(this.loginService.isLoggedIn()).pipe(
            filter((isLoggedIn) => isLoggedIn),
            switchMap(() => this.clearNotifications()),
            switchMap(() => this.resetBadgeCount()),
            switchMap(() =>
                this.pushNotificationBackendService.subscribeDevice(
                    {
                        deviceToken: this.deviceToken,
                        providerType: this.getProviderType(),
                    },
                    this.config.API_VERSION,
                    this.loginService.getToken() ?? '',
                ),
            ),
            map((): void => undefined),
            catchError((error) => {
                this.loggingService.error('Error while subscribing device for push notifications', error);
                return EMPTY;
            }),
        );
    }

    private unsubscribeDevice(token: string): Observable<void> {
        if (!this.deviceToken) {
            return EMPTY;
        }

        return this.pushNotificationBackendService
            .unsubscribeDevice(
                {
                    deviceToken: this.deviceToken,
                    providerType: this.getProviderType(),
                },
                this.config.API_VERSION,
                token,
            )
            .pipe(
                map((): void => undefined),
                catchError((error) => {
                    this.loggingService.error('Error while unsubscribing device from push notifications', error);
                    return EMPTY;
                }),
            );
    }

    private createNotificationItem(notification: PushNotificationSchema): IPxlNotificationItem {
        const notificationItem: IPxlNotificationItem = {
            pushNotificationSchema: notification,
            onOpenCallback: () =>
                firstValueFrom(
                    this.notificationActionPerformedHandler({ actionId: '', notification }).pipe(
                        defaultIfEmpty(undefined),
                        finalize(() => this.notificationService.removeNotification(notificationItem.pushNotificationSchema.id)),
                    ),
                ),
        };
        return notificationItem;
    }

    private notificationActionPerformedHandler(notification: ActionPerformed): Observable<void> {
        const data: PxlPushNotificationData = notification.notification.data;
        const activityStreamItemTypeId = data.activityStreamItemTypeId;
        const assignmentId = data['assignment.Id'] as string;
        const planId = data['assignment.PlanId'] as string;

        if (activityStreamItemTypeId === ActivityStreamItemType.MedicationAssignmentReminder) {
            return this.handleMedicationAssignmentReminder(assignmentId, planId);
        }
        if (activityStreamItemTypeId === ActivityStreamItemType.MedicationPlanConcluded) {
            return this.makeAppointment(data);
        }
        return this.handleActivityStreamNotification(data);
    }

    private handleMedicationAssignmentReminder(assignmentId: string, planId: string): Observable<void> {
        return this.fetchPlanById(planId).pipe(
            switchMap((plan) => {
                if (!plan) {
                    return EMPTY;
                }
                return this.openMedicationAssignmentDialog(assignmentId, plan);
            }),
        );
    }

    private fetchPlanById(planId: string): Observable<Plan | null> {
        if (!planId) {
            return of(null);
        }
        return from(this.medicationPlanService.getPlansByIds([planId])).pipe(
            map((plans) => plans.pop() || null),
            catchError((error) => {
                this.loggingService.error('Error fetching medication plan by ID:', error);
                return of(null);
            }),
        );
    }

    private openMedicationAssignmentDialog(assignmentId: string, plan: Plan): Observable<void> {
        const medicationId = plan.getPlanMedicationDetailsModel()?.medicationId;
        const referenceData = this.buildPlanReferenceData(plan);

        return from(this.medicationPlanService.getMedicationPlansByMedicationIds(toArray(medicationId), true, referenceData)).pipe(
            switchMap((plans) =>
                from(this.lazyDialogs.MedicationAssignmentDetails()).pipe(
                    map((MedicationAssignmentDetailsComponent) => {
                        const medicationPlansGroup = MedicationPlansGroup.create(plans);
                        this.dialogService.open(MedicationAssignmentDetailsComponent, {
                            assignmentId,
                            assignments: medicationPlansGroup.getAssignments(),
                        });
                    }),
                ),
            ),
            catchError((error) => {
                this.loggingService.error('Error fetching medication plans:', error);
                return EMPTY;
            }),
        );
    }

    private buildPlanReferenceData(plan: Plan): ReferenceDataForPlans {
        return {
            patients: toArray(plan.patient),
            medications: toArray(plan.medication),
            veterinarians: toArray(plan.veterinarian),
            appointments: toArray(plan.appointment),
            medicationRoutesOfAdministration: toArray(plan.medicationRouteOfAdministration),
            medicationUnits: toArray(plan.medicationUnit),
        };
    }

    private handleActivityStreamNotification(data: PxlPushNotificationData): Observable<void> {
        return this.getActivityStreamItemIds(data).pipe(
            switchMap((activityStreamItemIds) => from(this.activityStreamService.showActivityStreamItemDetails(activityStreamItemIds))),
            map((): void => undefined),
        );
    }

    private getActivityStreamItemIds(data: PxlPushNotificationData): Observable<string[]> {
        const { activityStreamItemGroupId } = data;

        if (activityStreamItemGroupId) {
            return from(this.activityStreamService.getActivities(undefined, undefined, activityStreamItemGroupId)).pipe(
                map((activityStreamDataModel) => activityStreamDataModel.activityStreamItems.map((item) => item.id)),
            );
        }

        return of(data.activityStreamItemId ? [data.activityStreamItemId] : []);
    }

    private getProviderType(): string {
        if (this.platformService.isAndroidPlatform()) return PushNotificationProviderTypes.Android;
        if (this.platformService.isIosPlatform()) return PushNotificationProviderTypes.Apple;
        return PushNotificationProviderTypes.Log;
    }

    private makeAppointment(data: PxlPushNotificationData): Observable<void> {
        const planId = data['plan.Id'] as string;

        return this.fetchPlanById(planId).pipe(
            switchMap((medicationPlan) => {
                if (medicationPlan) {
                    return from(this.lazyDialogs.AppointmentProcess()).pipe(
                        map((AppointmentProcessDialogComponent) => {
                            this.dialogService.open(AppointmentProcessDialogComponent, {
                                plan: medicationPlan,
                            });
                        }),
                    );
                }
                if (planId) {
                    this.loggingService.warn('Could not open appointment dialog - medication plan not available', planId);
                }
                return EMPTY;
            }),
        );
    }

    private handleAppResume(): Observable<void> {
        from(this.platformService.getDeviceInfo())
            .pipe(
                filter((deviceInfo) => deviceInfo.platform === 'web'),
                take(1),
            )
            .subscribe((updateReady) => {
                this.loggingService.info('updates found: ', updateReady);
            });

        return from(this.loginService.isLoggedIn()).pipe(
            filter((isLoggedIn) => isLoggedIn),
            switchMap(() => this.shouldClearOnResume()),
            filter((shouldClear) => shouldClear),
            switchMap(() => this.clearNotifications()),
            switchMap(() => this.resetBadgeCount()),
        );
    }

    private shouldClearOnResume(): Observable<boolean> {
        if (this.platformService.isIosPlatform()) {
            return of(true);
        }
        return this.getDeliveredNotifications().pipe(map((delivered) => !!delivered && delivered.notifications.length > 0));
    }

    private getDeliveredNotifications(): Observable<DeliveredNotifications | null> {
        return defer(() => PushNotifications.getDeliveredNotifications()).pipe(
            catchError((error) => {
                this.loggingService.error('Error while getting delivered notifications', error);
                return of(null);
            }),
        );
    }

    private clearNotifications(): Observable<void> {
        return defer(() => PushNotifications.removeAllDeliveredNotifications()).pipe(
            switchMap(() => Badge.clear()),
            map((): void => undefined),
            catchError((error) => {
                this.loggingService.error('Error while removing delivered notifications', error);
                return of(undefined);
            }),
        );
    }

    private resetBadgeCount(): Observable<void> {
        return this.pushNotificationBackendService
            .resetBadgeCountForDevice(this.deviceToken, this.getProviderType(), this.config.API_VERSION, this.loginService.getToken() ?? '')
            .pipe(
                map((): void => undefined),
                catchError((error) => {
                    this.loggingService.error('Error while resetting badge count for device', error);
                    return of(undefined);
                }),
            );
    }

    private registerAppListener() {
        void App.addListener('resume', () => {
            this.handleAppResume().subscribe();
        });
    }
}
